<template>
    <div class="flex flex-wrap lg:-mx-3">
        <template v-if="cardsReady">
            <card
                class="px-3 w-full | xs:w-1/3"
                :class="inventoryCardClass"
                :icon="card.icon"
                :card="card.card"
                :denominator="card.denominator"
                :title="card.title"
                :category="card.category"
                :key="card.name"
                @click="
                    $behavior.track('inventory', {
                        action: 'Click',
                        location: 'inventoryStatisticCardClicked',
                    })
                "
                v-for="card in activeCards"
            >
                <template #description v-if="card.componentDescription">
                    <component :is="card.componentDescription" :cards="cards" />
                </template>
                <template #description v-else-if="card.description">
                    <div class="flex justify-center text-lg">
                        <div class="text-xl">
                            {{ card.description }}
                        </div>
                    </div>
                </template>
                <template #popover v-if="card.informationTooltip">
                    <div class="text-center" v-text="card.informationTooltip" />
                </template>
            </card>
        </template>
    </div>

    <!-- Fix circleCI commit -->
</template>

<script>
    import Card from '@/components/dashboards/cards/Card.vue';

    // Mixins
    import { camelCase, round } from 'lodash-es';
    import $behavior from '@/plugins/mixpanel.ts';
    import InventoryCards from '../../mixins/InventoryCards.js';
    import DashboardCaching from '../../mixins/DashboardCaching.js';
    import { toMoney } from '../../utils/index.js';

    const buildDefaultCards = () => ({
        totalVehicles: {
            number: {
                count: null,
                colors: {
                    0: 'blue',
                },
            },
            new: {
                count: null,
            },
            demo: {
                count: null,
            },
            used: {
                count: null,
            },
            certified: {
                count: null,
            },
            unknown: {
                count: null,
            },
        },
        averagePrice: {
            number: {
                count: null,
                colors: {
                    0: 'blue',
                },
            },
            lowestPrice: {
                price: null,
            },
            highestPrice: {
                price: null,
            },
            totalValue: {
                price: null,
            },
        },
        averageDaysInInventory: {
            number: {
                count: null,
                colors: {
                    0: 'blue',
                },
            },
            moreThan7Days: {
                number: null,
            },
            moreThan14Days: {
                number: null,
            },
            moreThan30Days: {
                number: null,
            },
            moreThan60Days: {
                number: null,
            },
        },
        inStock: {
            number: {
                count: null,
                colors: {
                    0: 'blue',
                },
                name: 'inStock',
            },
        },
        accountSuppliers: [],
    });

    export default {
        components: { Card },

        mixins: [InventoryCards, DashboardCaching],

        props: {
            statusOptions: {
                type: Array,
                default: () => ['inStock', 'unavailable'],
            },
            accountSuppliers: {
                type: Array,
                default: () => {},
            },
        },

        data() {
            return {
                cards: buildDefaultCards(),
            };
        },

        computed: {
            $behavior() {
                return $behavior;
            },
            cardsReady() {
                return (
                    this.cards.totalVehicles.number.count !== null ||
                    this.cards.averagePrice.number.count !== null ||
                    this.cards.inStock.number.count !== null
                );
            },
            inventoryCards() {
                return {
                    totalVehicles: {
                        visible: true,
                    },
                    averagePrice: {
                        visible: true,
                    },
                    averageDaysInInventory: {
                        visible: false,
                    },
                    inStock: {
                        visible: true,
                    },
                    newVehicles: {
                        visible: true,
                    },
                };
            },
        },

        methods: {
            getMoneyValue($value) {
                return $value == null ? 'N/A' : toMoney($value ?? 0);
            },

            getCountValue($value) {
                return $value ?? 0;
            },

            getRoundedValue($value) {
                return $value == null ? 0 : round($value);
            },

            setCards(results = {}) {
                const inventoryAgeRange = results.inventoryAgeRange ?? {};
                const statuses = results.by_status ?? {};

                this.cards.totalVehicles.number.count = this.getCountValue(results.count);
                this.cards.totalVehicles.used.count = this.getCountValue(results.used);
                this.cards.totalVehicles.certified.count = this.getCountValue(results.certified);
                this.cards.totalVehicles.demo.count = this.getCountValue(results.demo);
                this.cards.totalVehicles.new.count = this.getCountValue(results.new);
                this.cards.totalVehicles.unknown.count = this.getCountValue(results.unknown);

                this.cards.averagePrice.number.count = this.getMoneyValue(results.averagePrice);
                this.cards.averagePrice.lowestPrice.price = this.getMoneyValue(results.lowestPrice);
                this.cards.averagePrice.highestPrice.price = this.getMoneyValue(
                    results.highestPrice
                );

                if (this.getMoneyValue(results.averagePrice) === 'N/A') {
                    this.cards.averagePrice.totalValue.price = 'N/A';
                } else {
                    this.cards.averagePrice.totalValue.price = this.getMoneyValue(
                        results.totalPrice
                    );
                }

                this.cards.averageDaysInInventory.number.count = this.getRoundedValue(
                    results.averageDaysInventory
                );
                this.cards.averageDaysInInventory.moreThan7Days.number = this.getCountValue(
                    inventoryAgeRange.moreThan7Days
                );
                this.cards.averageDaysInInventory.moreThan14Days.number = this.getCountValue(
                    inventoryAgeRange.moreThan14Days
                );
                this.cards.averageDaysInInventory.moreThan30Days.number = this.getCountValue(
                    inventoryAgeRange.moreThan30Days
                );
                this.cards.averageDaysInInventory.moreThan60Days.number = this.getCountValue(
                    inventoryAgeRange.moreThan60Days
                );

                this.cards.inStock.number.count = this.getCountValue(statuses.inStock);
                this.cards.accountSuppliers = this.accountSuppliers ?? [];

                Object.entries(this.statusOptions).forEach(([, value]) => {
                    const statusResult = statuses[camelCase(value)];
                    this.cards.inStock[camelCase(value)] = {
                        count: this.getCountValue(statusResult),
                        name: camelCase(value),
                    };
                });

                this.$nextTick(() => {
                    this.setCaching('cards', this.cards);
                });
            },
        },
    };
</script>
